// StatementsTable.tsx — Sprint 13. Table card with column defs +
// summary row + empty state. Stateless: rows + handlers come in
// from the orchestrator.
import React from 'react';
import { Button, Card, Popconfirm, Space, Table, Tag, Tooltip, Typography } from 'antd';
import {
  DownloadOutlined, EyeOutlined, FileTextOutlined, ReloadOutlined, SendOutlined,
} from '@ant-design/icons';
// Sprint 146 (R14-11) — every money cell here rendered `$1900.00` from a bare
// `$` + toFixed(2) template. One helper, en-CA/CAD, thousands-separated.
import { currency } from '../../utils/currency';

const { Text } = Typography;

export interface StatementsTableProps {
  rows:         any[];
  owners:       any[];
  totalBalance: number;

  onPreview:    (row: any) => void;
  onDownload:   (row: any) => void;
  onSendSingle: (id: string) => void;
  onRefresh:    () => void;
}

export const StatementsTable: React.FC<StatementsTableProps> = ({
  rows, owners, totalBalance,
  onPreview, onDownload, onSendSingle, onRefresh,
}) => {
  const columns = [
    {
      title: 'Owner', key: 'owner',
      render: (_: any, r: any) => {
        const owner = owners.find((o: any) => o.id === r.ownerId || o.userId === r.ownerId);
        return (
          <Space direction="vertical" size={0}>
            <Text strong>{owner?.name || '—'}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>{owner?.email || ''}</Text>
          </Space>
        );
      },
    },
    {
      title: 'Unit', key: 'unit', width: 80,
      render: (_: any, r: any) => {
        const owner = owners.find((o: any) => o.id === r.ownerId || o.userId === r.ownerId);
        return <Text strong>Unit {owner?.unitNumber || '—'}</Text>;
      },
    },
    {
      title: 'Period', dataIndex: 'period', key: 'period', width: 130,
      render: (v: string) => (
        <Tag style={{
          borderRadius: 20, fontSize: 11, padding: '0 10px',
          background: '#f3f4f6', color: '#374151',
          border: '1px solid #e5e7eb', fontWeight: 500,
        }}>
          {v || '—'}
        </Tag>
      ),
    },
    {
      title: 'Prev. Balance', dataIndex: 'previousBalance', key: 'prev',
      width: 120, align: 'right' as const,
      render: (v: number) => <Text type="secondary">{currency(v)}</Text>,
    },
    {
      // Sprint 134 (R12-8) — was "Payments", which promised money received in the
      // period. It is actually the sum of THIS period's fees that are settled,
      // derived from the fees table, not the condo.payments ledger: a fee paid in
      // July but belonging to May counts toward May, and a July payment against a
      // May fee shows nowhere in July. Renamed to what it is. ADR-129 records why
      // pointing it at the ledger is its own sprint.
      title: 'Fees Paid', dataIndex: 'payments', key: 'payments',
      width: 110, align: 'right' as const,
      render: (v: number) => <Text style={{ color: '#22c55e' }}>{currency(v)}</Text>,
    },
    {
      title: 'Balance Due', dataIndex: 'currentBalance', key: 'balance',
      width: 120, align: 'right' as const,
      render: (v: number) => (
        <Text strong style={{ color: v > 0 ? '#dc2626' : '#059669', fontSize: 14 }}>
          {currency(v)}
        </Text>
      ),
    },
    {
      title: 'Status', dataIndex: 'isSent', key: 'status', width: 100,
      render: (isSent: boolean) =>
        isSent ? (
          <Tag style={{
            borderRadius: 20, fontSize: 11, padding: '0 10px',
            background: '#ecfdf5', color: '#059669', border: '1px solid #bbf7d0',
          }}>Sent</Tag>
        ) : (
          <Tag style={{
            borderRadius: 20, fontSize: 11, padding: '0 10px',
            background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a',
          }}>Draft</Tag>
        ),
    },
    {
      title: 'Actions', key: 'actions', fixed: 'right' as const, width: 200,
      render: (_: any, r: any) => (
        <Space size={4} wrap>
          <Tooltip title="Preview statement">
            <Button size="small" icon={<EyeOutlined />} onClick={() => onPreview(r)}>View</Button>
          </Tooltip>
          <Button
            size="small"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => onDownload(r)}
            style={{ background: '#1890ff', borderColor: '#1890ff', color: '#fff' }}
          >
            PDF
          </Button>
          {!r.isSent && (
            <Tooltip title="Send by email">
              <Popconfirm title="Send this statement by email?" onConfirm={() => onSendSingle(r.id)}>
                <Button size="small" icon={<SendOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card
      style={{
        borderRadius: 10,
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        overflow: 'hidden',
      }}
      bodyStyle={{ padding: 0 }}
    >
      <Table
        columns={columns}
        dataSource={rows}
        rowKey="id"
        pagination={{ pageSize: 12, showSizeChanger: true }}
        scroll={{ x: 960 }}
        locale={{
          emptyText: (
            <Space direction="vertical" align="center" style={{ padding: '32px 0' }}>
              <FileTextOutlined style={{ fontSize: 40, color: '#d9d9d9' }} />
              <Text type="secondary" style={{ fontSize: 14 }}>
                No statements for this period — a statement appears once an owner
                has an outstanding fee in it.
              </Text>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={onRefresh}
                style={{ background: '#4f46e5', borderColor: '#4f46e5', color: '#fff', borderRadius: 8 }}
              >
                Refresh
              </Button>
            </Space>
          ),
        }}
        summary={() => rows.length > 0 ? (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={5}>
              <Text strong>Total ({rows.length} statements)</Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={5} align="right">
              <Text strong style={{ color: totalBalance > 0 ? '#dc2626' : '#059669' }}>
                {currency(totalBalance)}
              </Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={6} colSpan={2} />
          </Table.Summary.Row>
        ) : null}
      />
    </Card>
  );
};
